import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../assets/images/Logo smart farm1.jpg";
import Background from "../../assets/images/background-signup.png";
import { cleanOtherUsersData } from "../../utils/auth";

const languages = {
  ar: { label: "AR", flag: "TN" },
  fr: { label: "FR", flag: "FR" },
  en: { label: "EN", flag: "GB" },
  it: { label: "IT", flag: "IT" },
  wf: { label: "WF", flag: "SN" },
};

function Login() {
  const [credentials, setCredentials] = useState({ email: "", pwd: "" });
  const [rememberMe, setRememberMe] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [local, setLocal] = useState(localStorage.getItem("local") || "en");
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const changeLanguageHandler = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("local", lang);
    setLocal(lang);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMsgError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        redirect: "follow",
        body: JSON.stringify({
          password: credentials.pwd,
          email: credentials.email,
        }),
      });
      const result = await response.json();
      const data = result.result;

      if (result.errors?.length) {
        setMsgError(result.errors[0].msg);
        return;
      }

      if (result.type === "danger") {
        if (result.status === "400") setMsgError("Invalid password. Please try again.");
        else if (result.status === "404") setMsgError("Unauthorized. Please check your credentials.");
        else setMsgError("An unexpected error occurred. Please try again later.");
        return;
      }

      if (data) {
        cleanOtherUsersData();
        localStorage.setItem(
          "user",
          JSON.stringify({
            email: data.email,
            name: data.name,
            id: data.uid,
            role: data.role,
            offer_type: data.offer_type,
            pack: data.pack,
            avatar: data.upload_file_name,
            has_command: data.has_command,
            head: data.head,
          })
        );
        localStorage.setItem("token", JSON.stringify({ token: result.token }));
        localStorage.setItem("refreshToken", result.refreshToken);

        if (data.role === "ROLE_ADMIN") navigate("/admin/users");
        else if (data.role === "ROLE_USER" && data.offer_type === "1") navigate("/Dashboard");
        else navigate("/");

        window.location.reload();
      }
    } catch (error) {
      console.error("Login failed:", error.message);
      setMsgError("Login failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-page auth-login-page">
      <div className="auth-shell">
        <section className="auth-visual" style={{ backgroundImage: `url(${Background})` }}>
          <div className="auth-visual-shade" />
          <Link to="/" className="auth-brand" aria-label="SmartFarm">
            <img src={logo} alt="SmartFarm" />
          </Link>
          <div className="auth-visual-content">
            <span className="auth-eyebrow">SMARTFARM</span>
            <h1>{t("agriculture_intelligente")}</h1>
            <p>Des données utiles pour mieux suivre chaque terrain, chaque jour.</p>
            <div className="auth-feature-list">
              <span><i className="fas fa-broadcast-tower" />{t("sensors")}</span>
              <span><i className="far fa-calendar-alt" />{t("calendar")}</span>
              <span><i className="far fa-lightbulb" />{t("recommendations")}</span>
            </div>
          </div>
          <div className="auth-visual-footer">
            <i className="fas fa-shield-alt" />
            <span>Accès sécurisé à votre exploitation</span>
          </div>
        </section>

        <section className="auth-form-panel">
          <div className="auth-form-topbar">
            <span className="auth-secure-label"><i className="fas fa-leaf" /> SmartFarm</span>
            <Dropdown align="end" className="auth-language">
              <Dropdown.Toggle variant="light" id="login-language">
                <ReactCountryFlag countryCode={languages[local].flag} svg />
                <span>{languages[local].label}</span>
                <i className="fas fa-chevron-down" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {Object.entries(languages).map(([lang, language]) => (
                  <Dropdown.Item key={lang} active={local === lang} onClick={() => changeLanguageHandler(lang)}>
                    <ReactCountryFlag countryCode={language.flag} svg />
                    <span>{language.label}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="auth-form-content">
            <div className="auth-form-heading">
              <span className="auth-heading-icon"><i className="fas fa-user" /></span>
              <div>
                <span className="auth-eyebrow">SMARTFARM</span>
                <h2>{t("sign_in")}</h2>
                <p>{t("have_account")} <Link to="/sign-up">{t("Sign up")}</Link></p>
              </div>
            </div>

            {msgError && (
              <div className="auth-alert" role="alert">
                <i className="fas fa-exclamation-circle" />
                <span>{msgError}</span>
              </div>
            )}

            <form className="auth-form" autoComplete="on" onSubmit={handleSubmit}>
              <label className="auth-field">
                <span>{t("email")}</span>
                <div className="auth-input-wrap">
                  <i className="far fa-envelope" />
                  <input
                    type="email"
                    value={credentials.email}
                    placeholder="name@example.com"
                    autoComplete="email"
                    required
                    onChange={(event) => setCredentials({ ...credentials, email: event.target.value })}
                  />
                </div>
              </label>

              <label className="auth-field">
                <span>{t("Password")}</span>
                <div className="auth-input-wrap">
                  <i className="fas fa-lock" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={credentials.pwd}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    onChange={(event) => setCredentials({ ...credentials, pwd: event.target.value })}
                  />
                  <button type="button" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                    <i className={`far ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                  </button>
                </div>
              </label>

              <div className="auth-form-options">
                <label className="auth-checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => {
                      setRememberMe(event.target.checked);
                      localStorage.setItem("RememberMe", event.target.checked);
                    }}
                  />
                  <span>{t("keep_me")}</span>
                </label>
                <Link to="/forget-password">{t("forgot_pass")}</Link>
              </div>

              <button type="submit" className="auth-primary-button" disabled={isSubmitting}>
                {isSubmitting ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-arrow-right" />}
                <span>{t("sign_in")}</span>
              </button>
            </form>

            <div className="auth-trust-note">
              <i className="fas fa-lock" />
              <span>Vos informations sont protégées et utilisées uniquement pour sécuriser votre compte.</span>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Login;
